import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import { SelectChangeEvent } from "@mui/material";
import React, { useEffect } from "react";
import styled from "styled-components";
import { ActiveArea, ContentArea, HeaderArea } from "../styles/shared/page";
import { useAppSelector } from "../hooks/useAppSelector";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { setTab } from "../store/features/pages/home-page-slice";
import { HomeTabConstants } from "../constants/home-tab-constants";
import TableView, { ColumnConfig, FetchParams } from "../components/controls/table-view";
import { GoogleIcon, ICON_ID } from "../components/icons/google-icon";
import Button from "../components/controls/button";
import {
  fetchActiveOrders,
  fetchDeliveredOrders,
  fetchPendingOrders,
  resetActiveOrders,
  resetDeliveredOrders,
  resetPendingOrders,
} from "../store/features/ordering/order-slice";
import { fetchReturns, resetReturns } from "../store/features/returns/returns-slice";
import { useNavigate } from "react-router-dom";
import { TableContainer } from "../styles/shared/table";
import { currencyFormatter } from "../helpers/formatters";
import Dropdown from "../components/controls/dropdown";
import { ReturnStatus } from "../models/returns/applicant-return";

export default function Home() {
  const homeState = useAppSelector((state) => state.home);
  const orderState = useAppSelector((state) => state.order);
  const returnsState = useAppSelector((state) => state.returns);
  const branch = useAppSelector((state) => state.branch).selectedBranch;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [returnsStatus, setReturnsStatus] = React.useState<"All" | ReturnStatus>("All");

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    dispatch(resetActiveOrders());
    dispatch(resetDeliveredOrders());
    dispatch(resetPendingOrders());
    dispatch(resetReturns());
    dispatch(setTab(newValue));
  };

  const fetchMoreData = async (fetchParams: FetchParams) => {
    await dispatch(fetchActiveOrders({ branchId: branch!.id, fetchParams }));
  };
  const fetchDeliveredData = async (fetchParams: FetchParams) => {
    await dispatch(fetchDeliveredOrders({ branchId: branch!.id, fetchParams }));
  };

  const fetchPendingData = async (fetchParams: FetchParams) => {
    await dispatch(fetchPendingOrders({ branchId: branch!.id, fetchParams }));
  };

  const fetchReturnsData = async (fetchParams: FetchParams) => {
    await dispatch(
      fetchReturns({
        fetchParams,
        userOnly: true,
        status: returnsStatus === "All" ? undefined : returnsStatus,
        branchId: branch!.id,
      })
    );
  };

  const handleReturnsStatusChange = async (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as "All" | ReturnStatus;
    setReturnsStatus(value);
    await dispatch(resetReturns());
    await dispatch(
      fetchReturns({
        fetchParams: { reset: true },
        userOnly: true,
        status: value === "All" ? undefined : value,
        branchId: branch!.id,
      })
    );
  };

  const renderReturnsFilterBar = () => (
    <ReturnsFilterContainer>
      <Dropdown
        id="cboReturnStatus"
        label="Status"
        variant="outlined"
        autoWidth={false}
        value={returnsStatus}
        onChange={handleReturnsStatusChange}
        options={[
          { value: "All", label: "All" },
          { value: "New", label: "New" },
          { value: "Resolved", label: "Resolved" },
        ]}
        multiple={false}
        native={false}
        fullWidth={false}
      />
    </ReturnsFilterContainer>
  );

  const columnConfig: { [key: string]: ColumnConfig } = {
    id: {
      hidden: true,
    },
    branchId: {
      hidden: true
    },
    displayName: {
      order: 1,
      name: "Order",
      colspan: 2,
      render: (value: string) => (
        <div>
          <strong>{value}</strong>
        </div>
      ),
    },
    status: {
      hidden: true,
    },
    lineCount: {
      name: "Items",
      hideOnMobile: true,
      order: 2,
      render: (value: number) => <div>{value}</div>,
    },
    created: {
      hideOnMobile: true,
      name: "Order date",
      render: (value: Date) => (
        <div>
          {new Date(value).toLocaleString("en-gb", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          })}
        </div>
      ),
    },
    departmentName: {
      hideOnMobile: true,
      name: "Department",
      render: (value: string) => (
        <div>{value}</div>
      ),
    },
    subDepartmentName: {
      hideOnMobile: true,
      name: "SubDepartment",
      render: (value: string) => (
        <div>{value}</div>
      ),
    },

    totalAmount: {
      hideOnMobile: true,
      name: "Total Amount",
      render: (value: number) => (
        <div>
          {currencyFormatter.format(value)}
        </div>
      ),
    },
  };

  const returnsColumnConfig: { [key: string]: ColumnConfig } = {
    id: {
      hidden: true,
    },
    supplierId: {
      hidden: true,
    },
    supplierName: {
      name: "Supplier",
      order: 1,
    },
    supplierProductId: {
      hidden: true,
    },
    productName: {
      name: "Product",
      order: 2,
    },
    returnQuantity: {
      name: "Qty",
      order: 3,
      align: "center",
    },
    status: {
      name: "Status",
      order: 4,
    },
    branchId: {
      hidden: true,
    },
    branchName:{
      hidden:true
    },
    notes: {
      name: "Notes",
      order: 5,
      hideOnMobile: true,
      render: (value: string | null) => <div>{value ?? "-"}</div>,
    },
    createdBy: {
      name: "Created By",
      order: 6,
      hideOnMobile: true,
      render: (value: string | null) => <div>{value ?? "-"}</div>,
    },
    created: {
      name: "Created",
      order: 7,
      hideOnMobile: true,
      render: (value: Date) => (
        <div>
          {new Date(value).toLocaleString("en-gb", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
          })}
        </div>
      ),
    },
    updatedBy: {
      hidden: true,
    },
    updated: {
      hidden: true,
    },
  };

  const handleActionClick = (row: any) => {
    navigate(`/order/${row.id}`);
  };

  const renderActionButton = (row: any) => (
    <Button
      variant="outlined"
      id={row.Name}
      label="View"
      isPrimary={true}
      fullWidth={false}
      onClick={(event) => {
        event.stopPropagation();
        handleActionClick(row);
      }}
    />
  );

  return (
    <>
      <HeaderArea>
        <TabArea>
          <Tabs
            value={homeState.tab}
            onChange={handleChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab value={HomeTabConstants.activeOrders} label="Active Orders" />
            <Tab value={HomeTabConstants.pendingOrders} label="Pending Orders" />
            <Tab value={HomeTabConstants.orderHistory} label="Order History" />
            <Tab value={HomeTabConstants.returns} label="Returns" />
          </Tabs>
        </TabArea>
      </HeaderArea>
      <ContentArea>
        <ActiveArea>
          <TableContainer>
            {homeState.tab == HomeTabConstants.activeOrders && (
              <TableView
                id="orderTable"
                emptyText="There are no active orders"
                fetchData={(fetchParams) => fetchMoreData(fetchParams)}
                onRowClick={handleActionClick}
                totalCount={orderState.activeOrders?.count ?? 0}
                data={orderState.activeOrders?.orders ?? []}
                actionButton={renderActionButton}
                columnConfig={columnConfig}
                showHeaders={true}
                search={true}
              />
            )}
            {homeState.tab == HomeTabConstants.pendingOrders && (
              <TableView
                id="orderPendingTable"
                emptyText="There are no pending orders"
                fetchData={(fetchParams) => fetchPendingData(fetchParams)}
                onRowClick={handleActionClick}
                totalCount={orderState.pendingOrders?.count ?? 0}
                data={orderState.pendingOrders?.orders ?? []}
                actionButton={renderActionButton}
                columnConfig={columnConfig}
                showHeaders={true}
                search={true}
              />
            )}
            {homeState.tab == HomeTabConstants.orderHistory && (
              <TableView
                id="orderHistoryTable"
                emptyText="There are no orders history"
                fetchData={(fetchParams) => fetchDeliveredData(fetchParams)}
                onRowClick={handleActionClick}
                totalCount={orderState.deliveredOrders?.count ?? 0}
                data={orderState.deliveredOrders?.orders ?? []}
                actionButton={renderActionButton}
                columnConfig={columnConfig}
                showHeaders={true}
                search={true}
              />
            )}
            {homeState.tab == HomeTabConstants.returns && (
              <TableView
                id="returnsTable"
                emptyText="There are no returns"
                fetchData={(fetchParams) => fetchReturnsData(fetchParams)}
                filterBar={renderReturnsFilterBar}
                totalCount={returnsState.returns?.count ?? 0}
                data={returnsState.returns?.returns ?? []}
                columnConfig={returnsColumnConfig}
                showHeaders={true}
                search={true}
              />
            )}
          </TableContainer>
        </ActiveArea>
      </ContentArea>

    </>
  );
}


const TabArea = styled.div`
  padding: 20px;
`;

const ReturnsFilterContainer = styled.div`
  min-width: 180px;
`;
